import { z } from "zod";
import type { Operation } from "../types";

export const CHECK_RESULT_SCHEMA_VERSION = 1 as const;

const resultIdParams = z.object({ id: z.coerce.number().int().positive() });

export const checkResultSnapshotSchema = z.object({
  schemaVersion: z.literal(CHECK_RESULT_SCHEMA_VERSION),
  candidate: z.object({
    id: z.string().min(1),
    reviewId: z.string().min(1),
  }),
  workflow: z.object({ id: z.string().min(1) }),
  project: z.object({
    appId: z.number().int().positive(),
    name: z.string().min(1),
    root: z.string().min(1),
  }),
  gate: z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    command: z.string().min(1),
  }),
  outcome: z.object({
    status: z.enum(["PASS", "FAIL"]),
    ranAt: z.string().min(1),
    durationMs: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  evidence: z.object({
    basis: z.string().min(1),
    source: z.literal("workflow-evidence-snapshot"),
    limitation: z.string().min(1),
  }),
  receipt: z
    .object({
      present: z.literal(false),
      signed: z.literal(false),
      replayable: z.literal(false),
      limitation: z.string().min(1),
    })
    .or(
      z.object({
        present: z.literal(true),
        signed: z.literal(true),
        replayable: z.boolean(),
        receiptId: z.string().min(1),
        receiptPayloadDigest: z.string().min(1),
        acceptedContractDigest: z.string().min(1),
        attemptId: z.string().min(1),
        evidenceGraphDigest: z.string().min(1),
        signatureKeyId: z.string().min(1),
        assurance: z.enum(["PROSPECTIVE", "RETROSPECTIVE"]),
        limitation: z.string().min(1),
      }),
    ),
});

export const checkResultRecordSchema = checkResultSnapshotSchema.extend({
  id: z.number().int().positive(),
  recordedAt: z.string().min(1),
});

export type CheckResultSnapshot = z.infer<typeof checkResultSnapshotSchema>;
export type CheckResultRecord = z.infer<typeof checkResultRecordSchema>;

const get: Operation<z.infer<typeof resultIdParams>, void, void, CheckResultRecord> = {
  method: "GET",
  path: ({ id }) => `/api/check-results/${id}`,
  params: resultIdParams,
  response: checkResultRecordSchema,
};

export const checkResults = { get };
